import { useQuery } from '@apollo/client';
import { useFetchEQLFields } from '@modules/common/hooks';
import { FETCH_METADATA_FIELD_RELATIONS } from '@modules/integrationsV2/hooks/useFetchMetadataFieldRelations';
import { IntegrationsActionType } from '@modules/integrationsV2/store/integrationsActionType';
import { useIntegrationsStore } from '@modules/integrationsV2/store/integrationsStore';
import { getMetadataFieldRelationsByIntegrationType } from '@modules/integrationsV2/utils';
import { GetFieldRelationsInput, Query } from 'generated/graphql';
import { useEffect } from 'react';
import { useGetUser } from 'v2/core/services/user';

export const useSetMetadataFieldRelationsByTypeInStore = () => {
  const {
    tenant: { id: tenantID },
  } = useGetUser();

  const dispatch = useIntegrationsStore(({ dispatch }) => dispatch);

  const { fields: allEqlFields, isLoading: isEqlFieldsLoading } =
    useFetchEQLFields({
      fetchPolicy: 'cache-first', // We already fetch with cache-and-network at root level
    });

  const { loading: isMetaDataFieldsLoading, error } = useQuery<
    Query,
    GetFieldRelationsInput
  >(FETCH_METADATA_FIELD_RELATIONS, {
    fetchPolicy: 'cache-and-network',
    variables: {
      tenantID,
    },
    skip: allEqlFields.length === 0,
    onCompleted: (data) => {
      const fieldRelations = data?.getFieldRelations?.relations ?? [];
      const fieldRelationsByType = getMetadataFieldRelationsByIntegrationType({
        fieldRelations,
        allEqlFields,
      });

      dispatch({
        type: IntegrationsActionType.SET_DATA,
        payload: {
          fieldRelationsByType,
        },
      });
    },
  });

  const isLoading = isEqlFieldsLoading || isMetaDataFieldsLoading;

  useEffect(() => {
    dispatch({
      type: IntegrationsActionType.SET_VIEW,
      payload: {
        isFieldRelationsQueryInProgress: isLoading,
      },
    });

    return () => {
      dispatch({
        type: IntegrationsActionType.SET_VIEW,
        payload: {
          isFieldRelationsQueryInProgress: false,
        },
      });
    };
  }, [dispatch, isLoading]);

  return {
    isLoading: isLoading,
    error,
  };
};
